const MAX_CONNECTION_RETRIES = 10;

/**
 * Create and start a WebRTCCall with oneself, using the given ICE config
 * @param {MediaStream} [stream] - If provided, its tracks will automatically be attached to the pcA peer connection.
 * @param {RTCConfiguration} [iceConfig] - The configuration used when creating the peer connections.
 * @param {number} [bandwidthLimit] - The bandwidth limit to apply.
 * @returns {Promise<{stop: function, pcB: RTCPeerConnection, pcA: RTCPeerConnection}>} Resolves once the call is established.
 */
export const createCall = async({ stream, iceConfig, bandwidthLimit }) => {
  const pcA = new RTCPeerConnection(iceConfig);
  const pcB = new RTCPeerConnection(iceConfig);

  const hasGatheredCandidates = {
    pcA: false,
    pcB: false,
  };
  pcA.addEventListener('icecandidate', (event) => {
    if (event.candidate && event.candidate.candidate) {
      hasGatheredCandidates.pcA = true;
      return pcB.addIceCandidate(event.candidate);
    }
  });
  pcB.addEventListener('icecandidate', (event) => {
    if (event.candidate && event.candidate.candidate) {
      hasGatheredCandidates.pcB = true;
      return pcA.addIceCandidate(event.candidate);
    }
  });

  if (stream) {
    stream.getTracks().forEach(track => pcA.addTrack(track, stream));
  }

  const offer = await pcA.createOffer();
  await Promise.all([pcA.setLocalDescription(offer), pcB.setRemoteDescription(offer)]);
  const answer = await pcB.createAnswer();
  if (bandwidthLimit && answer.sdp) {
    answer.sdp = applyBandwidthLimit(answer.sdp, bandwidthLimit);
  }
  await Promise.all([pcB.setLocalDescription(answer), pcA.setRemoteDescription(answer)]);

  await new Promise((resolve, reject) => {
    let attempt = 1;
    const interval = setInterval(() => {
      if (hasGatheredCandidates.pcA && hasGatheredCandidates.pcB) {
        clearInterval(interval);
        resolve();
      } else if (attempt > MAX_CONNECTION_RETRIES) {
        clearInterval(interval);
        reject(new Error('no_valid_ice_candidates'));
      } else {
        attempt++;
      }
    }, 500);
  });

  const stop = () => {
    pcA.close();
    pcB.close();
  };

  return {
    stop,
    pcA,
    pcB,
  };
};

const applyBandwidthLimit = (sdp, videoBandwidth) => {
  const lines = sdp.split('\r\n');
  lines.forEach((line, lineIndex) => {
    if (line.includes('m=video')) {
      const bandwidthLine = (`b=AS:${videoBandwidth}`);
      lines.splice(lineIndex + 1, 0, bandwidthLine);
    }
  });
  sdp = lines.join('\r\n');
  return sdp;
};
